import type { IntlShape } from 'react-intl';

import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type {
  TWorkJourneyMatrixCellCode,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';

export type AttendanceObservationTone =
  | 'auj'
  | 'aui'
  | 'vac'
  | 'lm'
  | 'pgr'
  | 'psg'
  | 'pren'
  | 'postn'
  | 'rem'
  | 'abs';

export type AttendanceObservation = {
  code: TWorkJourneyMatrixCellCode;
  tone: AttendanceObservationTone;
  label: string;
};

type ObservationSource = {
  kind?: TWorkJourneyMatrixCellKind | null;
  code?: TWorkJourneyMatrixCellCode | null;
  exceptionType?: string | null;
  leaveType?: string | null;
  observation?: string | null;
  observations?: string | null;
  description?: string | null;
  hasActualStart?: boolean | null;
  attendanceCompleted?: boolean | null;
};

const ABSENCE_KINDS = new Set<TWorkJourneyMatrixCellKind>([
  'justified_absence',
  'unjustified_absence',
  'vacation',
  'medical_leave',
  'paid_leave',
  'unpaid_leave',
  'prenatal',
  'postnatal',
  'other_absence',
  'replacement',
  'replacement_out',
]);

const CODE_BY_TYPE: Record<
  string,
  { code: TWorkJourneyMatrixCellCode; kind: TWorkJourneyMatrixCellKind }
> = {
  unjustified_absence: { code: 'AUI', kind: 'unjustified_absence' },
  justified_absence: { code: 'AUJ', kind: 'justified_absence' },
  vacation: { code: 'VAC', kind: 'vacation' },
  medical_leave: { code: 'LM', kind: 'medical_leave' },
  paid_leave: { code: 'PGR', kind: 'paid_leave' },
  unpaid_leave: { code: 'PSG', kind: 'unpaid_leave' },
  prenatal: { code: 'PREN', kind: 'prenatal' },
  postnatal: { code: 'POSTN', kind: 'postnatal' },
  shift_replacement: { code: 'REM', kind: 'replacement' },
};

const TONE_BY_CODE: Partial<Record<TWorkJourneyMatrixCellCode, AttendanceObservationTone>> = {
  AUJ: 'auj',
  AUI: 'aui',
  VAC: 'vac',
  LM: 'lm',
  PGR: 'pgr',
  PSG: 'psg',
  PREN: 'pren',
  POSTN: 'postn',
  REM: 'rem',
  ABS: 'abs',
};

const LABEL_ID_BY_KIND: Partial<Record<TWorkJourneyMatrixCellKind, string>> = {
  justified_absence: 'work.direction.glossary.item.AJ',
  unjustified_absence: 'work.direction.glossary.item.AI',
  vacation: 'work.direction.glossary.item.VAC',
  medical_leave: 'work.direction.glossary.item.LM',
  paid_leave: 'work.direction.glossary.item.PGR',
  unpaid_leave: 'work.direction.glossary.item.PSGR',
  prenatal: 'work.direction.glossary.item.PREN',
  postnatal: 'work.direction.glossary.item.POSTN',
  replacement: 'workJourney.management.matrix.legend.rem',
  replacement_out: 'workJourney.management.matrix.legend.remOut',
  other_absence: 'workJourney.realTime.status.absent',
};

const resolveKindAndCode = (
  source: ObservationSource,
): { kind: TWorkJourneyMatrixCellKind; code: TWorkJourneyMatrixCellCode } | null => {
  if (source.kind && ABSENCE_KINDS.has(source.kind) && source.code) {
    return { kind: source.kind, code: source.code };
  }

  const typeKey =
    source.exceptionType || source.observation || source.observations || source.leaveType;
  if (typeKey && CODE_BY_TYPE[typeKey]) {
    return CODE_BY_TYPE[typeKey];
  }

  if (source.code && TONE_BY_CODE[source.code]) {
    const kindFromCode =
      (Object.entries(CODE_BY_TYPE).find(([, value]) => value.code === source.code)?.[1]
        .kind as TWorkJourneyMatrixCellKind | undefined) ?? 'other_absence';
    return { kind: kindFromCode, code: source.code };
  }

  return null;
};

export const resolveAttendanceObservation = (
  intl: IntlShape,
  source: ObservationSource,
): AttendanceObservation | null => {
  const resolved = resolveKindAndCode(source);
  if (!resolved) return null;

  const tone = TONE_BY_CODE[resolved.code];
  if (!tone) return null;

  const labelId = LABEL_ID_BY_KIND[resolved.kind];
  const label =
    (source.description || '').trim() ||
    (labelId ? intl.formatMessage({ id: labelId }) : resolved.code);

  return {
    code: resolved.code,
    tone,
    label,
  };
};

export const resolveAttendanceObservationFromRecord = (
  intl: IntlShape,
  record: IAttendanceRecord | null | undefined,
  cell?: (ObservationSource & {
    attendance?: {
      leaveType?: string | null;
      observations?: string | null;
      hasActualStart?: boolean;
    } | null;
  }) | null,
): AttendanceObservation | null =>
  resolveAttendanceObservation(intl, {
    kind: cell?.kind,
    code: cell?.code,
    exceptionType: cell?.exceptionType,
    leaveType: cell?.leaveType ?? cell?.attendance?.leaveType ?? record?.leaveType,
    observation: cell?.observation ?? undefined,
    observations: cell?.attendance?.observations ?? record?.observations ?? undefined,
    description: cell?.description,
    hasActualStart: cell?.attendance?.hasActualStart ?? cell?.hasActualStart,
    attendanceCompleted: cell?.attendanceCompleted,
  });
